<template>
  <div class="trip-card card">
    <div class="card-header" :style="tripTypeStyle">
      <div class="row align-items-center">
        <div class="trip-header-section">
          <h2 class="tripnumberheader">Heli #{{ tripNumber }}</h2>
          <div class="sorting-section">
            <label>Sort:</label>
            <div class="sorting-controls">
              <button @click="decrementSortingIndex">-</button>
              <span>{{ editableTripData.sortingindex }}</span>
              <button @click="incrementSortingIndex">+</button>
            </div>
          </div>
          <div class="trip-header-actions">
            <button class="btn btn-outline-primary trip-header-icon-btn" @click="openTripCopyModal" title="Copy entire trip to another date">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
              </svg>
            </button>
            <button class="btn btn-danger trip-header-icon-btn" @click="confirmDeletion">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash" viewBox="0 0 16 16">
                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
              </svg>
            </button>
          </div>
        </div>
        <select v-model="editableTripData.triptype" @change="updateTrip" :style="tripTypeStyle">
            <option value="Private">Private</option>
            <option value="Lodge">Lodge</option>
            <option value="Day Heli">Day Heli</option>
            <option value="Media">Media</option>
          </select>
      </div>
      <div class="row pilots-helicopters-section">
        <div class="pilots-section">
          <label>Pilot:</label>
          <select v-model="editableTripData.pilotId" class="form-select">
            <option disabled value="">Select a Pilot</option>
            <option v-for="pilot in tripPilots" :key="pilot.staffid" :value="pilot.staffid">
              {{ pilot.person.firstname }} {{ pilot.person.lastname }}{{ pilot.isDeleted ? ' (D)' : '' }}
            </option>
          </select>
        </div>
        <div class="helicopter-section">
          <label>Helicopter:</label>
          <select v-model="editableTripData.helicopterId" class="form-select">
            <option disabled value="">Select a Helicopter</option>
            <option v-for="helicopter in tripHelicopters" :key="helicopter.helicopterid" :value="helicopter.helicopterid">
              {{ helicopter.model }} ({{ helicopter.callsign }}){{ helicopter.isDeleted ? ' (D)' : '' }}
            </option>
          </select>
        </div>
      </div>
      
    </div>

    <div class="card-body">
      <div class="trip-groups-header">
        <h5 class="card-title">Heli Groups</h5>
        <button class="btn btn-primary" @click="createNewGroup">New Group</button>
      </div>
      <div class="trip-groups-container">
        <trip-group
          v-for="(group, index) in tripData.groups"
          :key="group.groupid"
          :group-id="group.groupid"
          :groupIndex="index"
          :heliNumber="tripNumber"
          :groupEndDate="formatDateForInput(group.end_date)"
          :guide="group.guide"
          :guide-additional="group.guideAdditional"
          :clients="group.clients"
          :all-guides="allGuides"
          :all-beacons="allBeacons"
          :selected-date = "selectedDate"
          :trip-id="tripData.tripId"
          @update-guides="(groupId, guideId, guideAdditionalId) => handleUpdateGuides(groupId, guideId, guideAdditionalId)"
          @updateEndDate="(data) => updateGroupDate(data)"
          @deleteGroup="() => handleDeleteGroup(group.groupid)"
          @removeGuide="guide => handleRemoveGuide(guide)"
          @removeClient="(client) => handleRemoveClient(client)"
          @copyGroup="(data) => handleCopyGroup(data)"
          @checkCopyConflict="(data) => handleCheckCopyConflict(data)"
        ></trip-group>
      </div>
      <label>Notes:</label>
      <div class="notes-container">
        <textarea v-model="noteContent" placeholder="Add trip notes here..."></textarea>
        <div class="notes-footer">
          <SavedMessage
            :visible="saveMessageVisible"
            :success="saveSuccess"
            :message="saveMessage"
          />
          <button class="btn btn-primary save-note" @click="saveNote">Save Note</button>
        </div>
      </div>
    </div>

    <!-- Trip Copy Modal -->
    <div v-if="showTripCopyModal" class="modal-overlay" @click.self="showTripCopyModal = false">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>Copy Trip to Date</h3>
        <button @click="showTripCopyModal = false" class="close-btn">&times;</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label>Target Date:</label>
          <DatePicker v-model="tripCopyTargetDate" @change="checkTripCopyConflicts" />
        </div>

        <div v-if="isCheckingTripConflict" class="checking-section">
          <div class="alert alert-info">
            Checking for conflicts on {{ tripCopyTargetDate }}...
          </div>
        </div>

        <div v-if="tripCopyConflict" class="conflict-section">
          <!-- Trip Already Copied Error -->
          <div v-if="tripCopyConflict.type === 'trip_already_copied'" class="alert alert-danger">
            <strong>Trip Already Copied:</strong> {{ tripCopyConflict.error }}
            <br><small>Please select a different date or edit the existing copy (Trip {{ tripCopyConflict.existingTripId }}).</small>
          </div>

          <!-- Helicopter/Pilot Conflict -->
          <div v-else class="alert alert-warning">
            <strong>Conflict Detected:</strong> A trip with the same helicopter and pilot already exists on {{ tripCopyTargetDate }}.
            <br><strong>Helicopter:</strong> {{ tripCopyConflict.helicopter?.model }} ({{ tripCopyConflict.helicopter?.callsign }})
            <br><strong>Pilot:</strong> {{ tripCopyConflict.pilot?.firstname }} {{ tripCopyConflict.pilot?.lastname }}
          </div>

          <!-- Conflict Resolution Options (only for helicopter/pilot conflicts) -->
          <div v-if="tripCopyConflict.type !== 'trip_already_copied'" class="conflict-options">
            <label>
              <input type="radio" v-model="tripConflictResolution" value="add_to_existing" />
              Copy to same heli, new groups
            </label>
            <label>
              <input type="radio" v-model="tripConflictResolution" value="create_new_trip" />
              Copy to a new heli
            </label>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button @click="showTripCopyModal = false" class="btn btn-secondary">Cancel</button>
        <button
          @click="copyTrip"
          class="btn btn-primary"
          :disabled="!tripCopyTargetDate ||
                    (tripCopyConflict && tripCopyConflict.type === 'trip_already_copied') ||
                    (tripCopyConflict && tripCopyConflict.type !== 'trip_already_copied' && !tripConflictResolution)"
        >
          {{ tripCopyConflict && tripCopyConflict.type === 'trip_already_copied' ? 'Cannot Copy - Already Exists' :
             tripCopyConflict ? 'Resolve & Copy' : 'Copy Trip' }}
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import TripGroup from './TripGroup.vue';
import TripDataService from '@/services/TripDataService';
import NotesDataService from '@/services/NotesDataService';
import StaffDataService from '@/services/StaffDataService';
import SavedMessage from '../Notes/SavedMessage.vue';
import DatePicker from '../Shared/DatePicker.vue';

export default {
  props: {
    tripData: {
      type: Object,
      required: true,
    },
    tripNumber: {
      type: Number,
      required: true,
    },
    allPilots: {
      type: Array,
      required: true,
    },
    allGuides: {
      type: Array,
      required: true,
    },
    allHelicopters: {
      type: Array,
      required: true,
    },
    allBeacons: Array,
    selectedDate: {
      type: String,
      required: true,
    }
  },
  mounted() {
    this.fetchNote();
    this.fetchLoggedInPersonId();
    this.fetchTripHelicopters();
    this.fetchTripPilots();
  },
  components: {
    TripGroup,
    SavedMessage,
    DatePicker
  },
  data() {
    return {
      selectedGuideId: '',
      editableTripData: {
        pilotId: this.tripData.pilot ? this.tripData.pilot.staffid : '',
        helicopterId: this.tripData.helicopter ? this.tripData.helicopter.helicopterid : '',
        triptype: this.tripData.triptype || '',
        notes: this.tripData.notes ? this.tripData.notes : [],
        guides: this.tripData.guides || [],
        sortingindex: this.tripData.sortingindex || 1
      },
      totalGearWeight: 0,
      noteContent: '',
      noteId: null,
      loggedInPersonId: null,
      tripGroups: this.tripData.groups || [],
      saveMessageVisible: false,
      saveSuccess: true,
      saveMessage: '',
      // Trip copy functionality
      showTripCopyModal: false,
      tripCopyTargetDate: '',
      tripCopyConflict: null,
      tripConflictResolution: '',
      isCheckingTripConflict: false,
      // Trip-specific helicopter data (includes soft-deleted if assigned)
      tripHelicopters: [],
      // Trip-specific pilot data (includes soft-deleted if assigned)
      tripPilots: []
    };
  },
  watch: {
    'editableTripData.pilotId': function (newVal, oldVal) {
      if (newVal !== oldVal) this.updateTrip();
    },
    'editableTripData.helicopterId': function (newVal, oldVal) {
      if (newVal !== oldVal) this.updateTrip();
    },
    'editableTripData.triptype': function (newVal, oldVal) {
      if (newVal !== oldVal) this.updateTrip();
    },
    selectedGuideId(newValue) {
      if (newValue) {
        const guide = this.allGuides.find(g => g.staffid == newValue);
        if (guide && !this.tripData.guides.some(g => g.personid === guide.person.personid)) {
          this.tripData.guides.push({ ...guide.person });
          this.updateTrip();
        }
        this.selectedGuideId = '';
      }
    },
  },
  computed: {
    availableGuides() {
      return this.allGuides.map(guide => {
        const isGuideSelected = this.tripData.guides.some(g => g.personid === guide.person.personid);
        return {
          ...guide,
          isDisabled: isGuideSelected
        };
      });
    },
    tripTypeStyle() {
      let borderColor = '';
      let backgroundColor = '';

      switch (this.editableTripData.triptype) {
        case 'Private':
          borderColor = 'var(--private-event-color)';
          backgroundColor = 'rgba(247, 191, 47, 0.3)';
          break;
        case 'Lodge':
          borderColor = 'var(--lodge-event-color)';
          backgroundColor = 'rgba(237, 98, 64, 0.3)';
          break;
        case 'Day Heli':
          borderColor = 'var(--day-event-color)';
          backgroundColor = 'rgba(109, 123, 156, 0.3)';
          break;
        case 'Media':
          borderColor = 'var(--minor-media-event-color)';
          backgroundColor = 'rgba(95, 184, 100, 0.3)';
          break;
        default:
          borderColor = '#ccc';
          backgroundColor = 'rgba(204, 204, 204, 0.3)';
      }

      return {
        border: `2px solid ${borderColor}`,
        backgroundColor: `${backgroundColor}`,
      };
    }
  },
  methods: {
    createNewGroup() {
      const newGroupData = {
        tripId: this.tripData.tripId,
        startDate: this.selectedDate,
        endDate: this.selectedDate
      };
      TripDataService.createGroup(newGroupData)
        .then(response => {
          this.tripData.groups = [...this.tripData.groups, response.data];
          this.$emit('clientRemoved');
        })
        .catch(error => {
          console.error('Error creating new group:', error);
        });
    },
    handleDeleteGroup(groupId) {
      const confirmation = window.confirm("Deleting the group will unassign all clients in the group, are you sure you want to delete this group?");

      if (confirmation) {
        // TODO: Get actual logged-in user ID, for now using default admin ID (1)
        const deletedBy = 1; // This should come from authentication/user context

        TripDataService.deleteGroup(groupId, deletedBy)
          .then(() => {
            this.tripData.groups = this.tripData.groups.filter(group => group.groupid !== groupId);
            this.$emit('clientRemoved');
          })
          .catch(error => {
            console.error('Error deleting group:', error);
          });
      }
    },
    handleRemoveGuide(guide) {
      console.log(guide);
    },
    handleRemoveClient(clientInfo) {
      // TODO: Get actual logged-in user ID, for now using default admin ID (1)
      const deletedBy = 1; // This should come from authentication/user context

      TripDataService.removeClientFromGroup(clientInfo.groupId, clientInfo.tripClientId, this.selectedDate, deletedBy)
        .then(() => {
          this.$emit('clientRemoved', clientInfo);
        })
        .catch(error => {
          console.error('Error removing client from group:', error);
        });
    },
    handleUpdateGuides(groupId, guideId, guideAdditionalId) {
      console.log(
        `Updating Group ${groupId} for Date: ${this.selectedDate} with Guide: ${guideId} and Additional Guide: ${guideAdditionalId}`
      );
      TripDataService.updateGroupGuides(groupId, guideId, guideAdditionalId, this.selectedDate)
        .then(() => {
          console.log(`Successfully updated guides for group ${groupId} on date ${this.selectedDate}`);
          this.$emit('guidesUpdated', groupId);
        })
        .catch(error => {
          console.error("Error updating guides:", error);
        });
    },
    fetchLoggedInPersonId() {
      const email = sessionStorage.getItem('email');
      if (email) {
        StaffDataService.findByEmail(email)
          .then(response => {
            this.loggedInPersonId = response.data.personid;
          })
          .catch(error => {
            console.error('Error fetching staff info:', error);
          });
      }
    },
    fetchNote() {
      NotesDataService.getTripNote(this.tripData.tripId)
        .then(response => {
          if (response.data && response.data.text !== null) {
            this.noteContent = response.data.text;
            this.noteId = response.data.noteid;
          } else {
            this.noteContent = '';
            this.noteId = null;
          }
        })
        .catch(error => {
          if (error.response && error.response.status === 404) {
            this.noteContent = '';
            this.noteId = null;
          } else {
            console.error('Error fetching note:', error);
          }
        });
    },
    saveNote() {
      this.loggedInPersonId = 1;

      const noteData = {
        text: this.noteContent,
        personid: this.loggedInPersonId,
        type: 'tripnote',
        itemid: this.tripData.tripId
      };

      if (this.noteId) {
        NotesDataService.update(this.noteId, noteData)
          .then(() => {
            this.updateTrip();
            this.showSaveMessage(true, 'Saved');
          })
          .catch(error => {
            console.error("Error updating note:", error);
            this.showSaveMessage(false, 'Failed to save');
          });
      } else {
        NotesDataService.create(noteData)
          .then(response => {
            this.noteId = response.data.noteid;
            this.updateTrip();
            this.showSaveMessage(true, 'Saved');
          })
          .catch(error => {
            console.error("Error creating note:", error);
            this.showSaveMessage(false, 'Failed to save');
          });
      }
    },
    showSaveMessage(success, message) {
      this.saveSuccess = success;
      this.saveMessage = message;
      this.saveMessageVisible = true;
      setTimeout(() => {
        this.saveMessageVisible = false;
      }, 3000);
    },
    handleGearWeightUpdate(newGearWeight) {
      console.log(newGearWeight);
    },
    updateTrip() {
      const updatedTripData = {
        pilotid: this.editableTripData.pilotId,
        helicopterid: this.editableTripData.helicopterId,
        triptype: this.editableTripData.triptype,
        sortingindex: this.editableTripData.sortingindex,
        scheduleUpdates: [
          {
            trip_date: this.selectedDate, // Use the selectedDate from props
            pilotid: this.editableTripData.pilotId, // Use the editableTripData pilotId
            helicopterid: this.editableTripData.helicopterId, // Use the editableTripData helicopterId
            triptype: this.editableTripData.triptype
          }
        ]
      };

      console.log('trip data to update');
      console.log(updatedTripData);

      TripDataService.update(this.tripData.tripId, updatedTripData)
        .then(() => {
          console.log('Trip updated successfully');
          this.$emit('updateTrip'); // Emit event to refresh trips
        })
        .catch(error => {
          console.error('Error updating trip:', error);
        });
    },
    updateGroupDate({ groupId, newEndDate }) {
      const tripId = this.tripData.tripId;
      TripDataService.continueTripTill({ groupId, tripId, newEndDate })
      // eslint-disable-next-line no-unused-vars
        .then(response => {
          console.log("Trip continued successfully:", response.data);
          this.$emit('groupDateUpdated', { groupId, newEndDate });
          // Refresh the trips to show the new continued trips
          this.$emit('refreshTrips');
        })
        .catch(error => {
          console.error("Error continuing trip:", error);
        });
    },
    removeGuide(guideToRemove) {
      this.tripData.guides = this.tripData.guides.filter(guide => guide.personid !== guideToRemove.personid);
      this.updateTrip();
    },
    formatDateForInput(dateString) {
      if (!dateString) return '';
      // Convert ISO date string to YYYY-MM-DD format for HTML date input
      return new Date(dateString).toISOString().split('T')[0];
    },
    async handleCheckCopyConflict(data) {
      const { tripId, groupId, targetDate, callback } = data;

      try {
        // Make a test call to check for conflicts
        await TripDataService.copyTripToDate({
          tripId,
          targetDate,
          groupId: groupId || null, // Pass the specific groupId for conflict checking
          conflictResolution: null
        });

        // No conflict if we get here
        callback(null, { hasConflict: false });

      } catch (error) {
        if (error.response && error.response.status === 409) {
          // Conflict detected - pass it to callback
          callback(error, null);
        } else {
          console.error("Error checking for conflicts:", error);
          callback(error, null);
        }
      }
    },
    async handleCopyGroup(data) {
      const { groupId, targetDate, conflictResolution } = data;
      const tripId = this.tripData.tripId;

      try {
        const response = await TripDataService.copyTripToDate({
          tripId,
          targetDate,
          groupId,
          conflictResolution
        });

        console.log("Group copied successfully:", response.data);

        // Emit event to refresh trips
        this.$emit('refreshTrips');

        // Show success message
        alert(`Successfully copied group to ${targetDate}!`);

      } catch (error) {
        if (error.response && error.response.status === 409) {
          // This is a conflict that should be handled by the TripGroup component
          throw error;
        } else {
          console.error("Error copying group:", error);
          alert("Error copying group: " + (error.response?.data?.message || error.message));
        }
      }
    },
    removeClient(index) {
      this.tripData.clientids.splice(index, 1);
      this.updateTrip();
    },
    removeReservation(reservationId) {
      this.$emit('removeReservationFromTrip', {
        tripId: this.tripData.tripId,
        reservationId: reservationId
      });
      this.updateTrip();
    },
    confirmDeletion() {
      if (window.confirm("Are you sure you want to delete this trip? This action cannot be undone.")) {
        this.deleteTrip();
      }
    },
    deleteTrip() {
      // TODO: Get actual logged-in user ID, for now using default admin ID (1)
      const deletedBy = 1; // This should come from authentication/user context

      TripDataService.delete(this.tripData.tripId, deletedBy)
        .then(() => {
          this.$emit('tripDeleted', this.tripData.tripId);
          this.updateTrip();
        })
        .catch(error => {
          console.error("Error deleting trip:", error);
        });
    },
    incrementSortingIndex() {
      if (this.editableTripData.sortingindex < 5) {
        this.editableTripData.sortingindex++;
      } else {
        this.editableTripData.sortingindex = 1;
      }
      this.updateTrip();
    },
    decrementSortingIndex() {
      if (this.editableTripData.sortingindex > 1) {
        this.editableTripData.sortingindex--;
      } else {
        this.editableTripData.sortingindex = 5;
      }
      this.updateTrip();
    },
    // Trip copy functionality
    openTripCopyModal() {
      this.showTripCopyModal = true;
      this.tripCopyTargetDate = this.selectedDate; // Default to current selected date
      this.tripCopyConflict = null;
      this.tripConflictResolution = '';
      this.isCheckingTripConflict = false;
    },
    async checkTripCopyConflicts() {
      if (!this.tripCopyTargetDate) {
        this.tripCopyConflict = null;
        return;
      }

      this.isCheckingTripConflict = true;
      this.tripCopyConflict = null;
      this.tripConflictResolution = '';

      try {
        // Make a test call to check for conflicts (no groupId means check entire trip)
        await TripDataService.copyTripToDate({
          tripId: this.tripData.tripId,
          targetDate: this.tripCopyTargetDate,
          groupId: null, // null means check entire trip
          conflictResolution: null
        });

        // No conflict if we get here
        this.isCheckingTripConflict = false;

      } catch (error) {
        this.isCheckingTripConflict = false;

        if (error.response && error.response.status === 409) {
          // Check the type of conflict
          const responseData = error.response.data;
          if (responseData.conflictType === 'trip_already_copied') {
            // Trip already copied
            this.tripCopyConflict = {
              type: 'trip_already_copied',
              error: responseData.error,
              existingTripId: responseData.existingTripId,
              targetDate: responseData.targetDate
            };
          } else if (responseData.hasConflict && responseData.conflict) {
            // Helicopter/pilot conflict
            this.tripCopyConflict = {
              type: 'helicopter_pilot_conflict',
              helicopter: responseData.conflict.existingTrip?.helicopter,
              pilot: responseData.conflict.existingTrip?.pilot,
              existingTripId: responseData.conflict.existingTrip?.tripId
            };
          } else {
            // Other conflict types
            this.tripCopyConflict = {
              type: 'unknown_conflict',
              error: responseData.error || responseData.message
            };
          }
        } else {
          console.error('Error checking trip copy conflicts:', error);
        }
      }
    },
    async copyTrip() {
      if (!this.tripCopyTargetDate) return;

      try {
        const response = await TripDataService.copyTripToDate({
          tripId: this.tripData.tripId,
          targetDate: this.tripCopyTargetDate,
          groupId: null, // null means copy entire trip
          conflictResolution: this.tripConflictResolution || ''
        });

        console.log("Trip copied successfully:", response.data);

        // Close modal and reset
        this.showTripCopyModal = false;
        this.tripCopyTargetDate = '';
        this.tripCopyConflict = null;
        this.tripConflictResolution = '';

        // Emit event to refresh trips
        this.$emit('refreshTrips');

        // Show success message
        alert(`Trip copied successfully to ${this.tripCopyTargetDate}! New trip ID: ${response.data.targetTripId}`);

      } catch (error) {
        console.error('Error copying trip:', error);
        if (error.response && error.response.data) {
          alert(`Error copying trip: ${error.response.data.message || error.response.data.error}`);
        } else {
          alert('An error occurred while copying the trip.');
        }
      }
    },
    // Fetch helicopters specific to this trip (includes soft-deleted if assigned)
    async fetchTripHelicopters() {
      try {
        const response = await TripDataService.fetchHelicoptersForTrip(this.tripData.tripId);
        this.tripHelicopters = response.data;
      } catch (error) {
        console.error('Error fetching trip helicopters:', error);
        // Fallback to the general helicopter list if trip-specific fetch fails
        this.tripHelicopters = this.allHelicopters || [];
      }
    },
    // Fetch pilots specific to this trip (includes soft-deleted if assigned)
    async fetchTripPilots() {
      try {
        const response = await TripDataService.fetchPilotsForTrip(this.tripData.tripId);
        this.tripPilots = response.data;
      } catch (error) {
        console.error('Error fetching trip pilots:', error);
        // Fallback to the general pilot list if trip-specific fetch fails
        this.tripPilots = this.allPilots || [];
      }
    }
  }
};
</script>

<style scoped>
@media (min-width: 768px) {
  .trip-card .card-header > .pilot-helicopter-container {
    display: flex;
    flex-wrap: wrap;
  }

  .trip-card .card-header > .pilot-helicopter-container > .pilots-section,
  .trip-card .card-header > .pilot-helicopter-container > .helicopter-section {
    flex: 1;
    padding-right: 5px;
  }

  .trip-card .card-header > .pilot-helicopter-container > .helicopter-section {
    padding-right: 0;
  }
}

.trip-card {
  box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2);
  transition: 0.3s;
  border-radius: 5px;
}

.trip-card:hover {
  box-shadow: 0 8px 16px 0 rgba(0,0,0,0.2);
}

.trip-groups-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.trip-groups-container {
  margin-bottom: 15px;
}

.trip-header-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.btn-primary {
  background-color: var(--primary-text-eph-color);
  padding: 5px 10px;
  font-size: 14px;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.pilot-helicopter-container {
  display: flex;
  justify-content: space-between;
}

.pilots-section, .helicopter-section {
  flex: 1;
  padding-right: 5px;
}

.helicopter-section {
  padding-right: 0;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3e6ea;
  position: relative;
}

.card-body {
  padding: 15px;
}

.notes-container {
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

.notes-container textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: vertical;
  min-height: 80px;
  padding: 10px;
  font-family: inherit;
  font-size: 14px;
}

.notes-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-top: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  position: relative;
}

.notes-footer .save-note {
  font-size: 12px;
  padding: 6px 12px;
}

.selected-guides {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.guide-tag {
  padding: 5px 10px;
  margin-right: 5px;
  margin-bottom: 5px;
  background-color: #e9ecef;
  border-radius: 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.remove-guide {
  background-color: transparent;
  border: none;
  margin-left: 8px;
  cursor: pointer;
}

.selected-clients {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.client-tag {
  padding: 5px 10px;
  margin-right: 5px;
  margin-bottom: 5px;
  background-color: #e9ecef;
  border-radius: 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.remove-client {
  background-color: transparent;
  border: none;
  margin-left: 8px;
  cursor: pointer;
}

.reservations-section {
  background-color: #f8f9fa;
  border-top: 2px solid #e9ecef;
  padding-top: 20px;
}

.reservations-section .card-title {
  color: var(--primary-text-eph-color);
  margin-bottom: 15px;
}

.selected-reservations {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.reservation-tag {
  padding: 5px 10px;
  margin-right: 5px;
  margin-bottom: 5px;
  background-color: #e9ecef;
  border-radius: 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.tripnumberheader {
  color: var(--primary-text-eph-color);
}

.remove-reservation {
  background-color: transparent;
  border: none;
  margin-left: 8px;
  cursor: pointer;
  color: #dc3545;
}

.remove-reservation:hover {
  text-decoration: underline;
}

select, textarea {
  width: 100%;
  margin-bottom: 10px;
}

.sorting-section {
  display: flex;
  align-items: center;
  margin-left: 20px;
}

.sorting-controls {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.sorting-controls button {
  background-color: transparent;
  border: 1px solid #ccc;
  cursor: pointer;
  padding: 0 10px;
  margin: 0 5px;
  border-radius: 4px;
  height: 34px;
}

.sorting-controls span {
  font-size: 18px;
  padding: 0 10px;
}
.trip-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trip-header-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.375rem;
}

.trip-header-icon-btn svg {
  pointer-events: none;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 10px;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
}

.close-btn:hover {
  color: #000;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid #dee2e6;
}

</style>
